import { useRef } from 'react';
import { apiFetch } from '../api/client';
import { askHelp, type HelpResponse } from '../api/help';
import { rateLimitError } from '../utils/apiErrors';
import { advanceKey, createPendingKeys } from '../utils/idempotencyKey';
import { transcribeAudio as transcribeAudioUtil } from '../utils/transcribe';
import type { WordConfidence } from '../speech';

export interface LessonPart {
  id: string;
  label: string;
}

interface Step {
  step_index: number;
  action_type: string;
  content: Record<string, unknown>;
  audio?: {
    formId: string;
    speed: 'natural' | 'slow';
  };
}

/** A phrase the learner already got right, as kept by the server (no learner speech, TR-049). */
export interface LearnedPhrase {
  seq: number;
  form_id: string;
  text: string;
  gloss: string | null;
  part_id: string | null;
}

export interface StartLessonResponse {
  run_id: string;
  type: 'lesson' | 'practice';
  context_version_id: string;
  context_id: string;
  context_name: string;
  language_code: string;
  /** Null on a resume that ended the lesson (`completed`). */
  step: Step | null;
  step_version: number;
  total_steps: number;
  /** Only on a resume: moving on from the last prompt finished the lesson. */
  completed?: boolean;
  /** Only on a resume: the "where you left off" recap. */
  recap?: RecapEntry[];
  /** Only on a lesson resume: every phrase already learned, for the side rail (#347). */
  learned?: LearnedPhrase[];
  // The required parts the run covers, in script order; empty for practice (BR-039).
  parts: LessonPart[];
  policy: Record<string, unknown>;
}

export interface NextStepResponse {
  completed: boolean;
  step?: Step;
  step_version: number;
  assessment?: Record<string, unknown>;
}

export interface GenerateContentResponse {
  content: {
    text: string;
  };
  step_version: number;
}

export interface AudioUrlResponse {
  url: string;
}

export interface TranscribeAudioResponse {
  transcript: string;
  confidence: number;
  duration_ms: number;
  words?: WordConfidence[];
}

export interface ASRResult {
  transcript: string;
  /** Engine confidence, or `null` when the engine has no trustworthy score. */
  confidence: number | null;
  /** The learner asked for help with the phrase (BR-052); sent as the `ask_help` event. */
  askHelp?: boolean;
}

export type RecapKind =
  | 'context'
  | 'scene_beat'
  | 'meaning'
  | 'cue'
  | 'persona_line'
  | 'learner'
  | 'feedback';

export type RecapOutcome = 'success' | 'skipped' | 'tried_again';

/** One turn of the "where you left off" recap. A check-in entry never carries `text` (BR-043). */
export interface RecapEntry {
  seq: number;
  kind: RecapKind;
  text?: string;
  outcome?: RecapOutcome;
  part_id?: string | null;
}

/** `GET .../resumable`: is there an unfinished run the learner can pick up? */
export interface ResumableResponse {
  resumable: boolean;
  run_id?: string;
  step_index?: number;
  total_steps?: number;
  last_active_at?: string;
  recap?: RecapEntry[];
}

/** A resumable run the learner is being offered (`resumable` is true and `run_id` is set). */
export interface ResumeOffer {
  run_id: string;
  step_index: number;
  total_steps: number;
  last_active_at?: string;
  recap: RecapEntry[];
}

/**
 * The run can no longer be resumed (409 `not_resumable`): the caller starts a fresh run instead. Another 409 is a
 * version conflict from a second tap or tab, which must not abandon the run the other request just resumed.
 */
export const isNotResumable = async (response: Response): Promise<boolean> => {
  if (response.status !== 409) return false;
  try {
    const body = await response.clone().json();
    return body?.detail?.error === 'not_resumable';
  } catch {
    return false;
  }
};

/** The resume window has passed (409): the caller starts a fresh run instead. */
export class ResumeExpiredError extends Error {
  constructor() {
    super('This run can no longer be resumed');
    this.name = 'ResumeExpiredError';
  }
}

/** Reads a `ResumableResponse` into an offer, or null when nothing can be resumed. */
export const toResumeOffer = (data: ResumableResponse): ResumeOffer | null =>
  data.resumable && data.run_id
    ? {
        run_id: data.run_id,
        step_index: data.step_index ?? 0,
        total_steps: data.total_steps ?? 0,
        last_active_at: data.last_active_at,
        recap: data.recap ?? [],
      }
    : null;

/** Which run family the hook talks to: lessons, or the practice session. */
export type RunMode = 'lesson' | 'practice';

export const useLessonApi = (
  mode: RunMode = 'lesson'
): {
  startLesson: (
    lessonId: string,
    signal?: AbortSignal
  ) => Promise<StartLessonResponse>;
  /** Is there an unfinished run for this lesson? Throws on a failed request. */
  getResumable: (
    lessonId: string,
    signal?: AbortSignal
  ) => Promise<ResumableResponse>;
  /** Picks the run up at its current step; throws `ResumeExpiredError` past the window. */
  resumeRun: (
    runId: string,
    signal?: AbortSignal
  ) => Promise<StartLessonResponse>;
  nextStep: (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult,
    signal?: AbortSignal
  ) => Promise<NextStepResponse>;
  generateContent: (
    runId: string,
    stepVersion: number,
    signal?: AbortSignal
  ) => Promise<GenerateContentResponse>;
  fetchAudioUrl: (
    formId: string,
    speed: 'natural' | 'slow',
    signal?: AbortSignal
  ) => Promise<string>;
  transcribeAudio: (
    audioBlob: Blob,
    languageCode?: string,
    signal?: AbortSignal,
    phraseHints?: string[] | null
  ) => Promise<TranscribeAudioResponse>;
  /** What did the character just say? Sends the persona line's form id. */
  askHelp: (
    runId: string,
    formId: string | null,
    signal?: AbortSignal
  ) => Promise<HelpResponse>;
} => {
  const practice = mode === 'practice';
  // Starting a run has no natural identity: a retry after a failure reuses the key, a later start gets a new one.
  const startKeys = useRef(createPendingKeys()).current;

  const startLesson = async (
    lessonId: string,
    signal?: AbortSignal
  ): Promise<StartLessonResponse> => {
    // Check if aborted before doing any async work
    if (signal?.aborted) {
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }

    const startPath = practice
      ? '/practice/start'
      : `/lessons/${lessonId}/start`;
    const idemKey = startKeys.keyFor(startPath);
    // An aborted start is abandoned, not retried (e.g. StrictMode's mount/cleanup/mount): drop its key right
    // away so the next start is a new action rather than a duplicate of one the server is still processing.
    signal?.addEventListener('abort', () => startKeys.done(startPath), {
      once: true,
    });
    const response = await apiFetch(startPath, {
      method: 'POST',
      headers: { 'Idem-Key': idemKey },
      body: JSON.stringify({}), // Empty body as required by FastAPI
      signal, // Pass abort signal to fetch
    });

    if (!response.ok) {
      let errorMessage = `Failed to start lesson: ${response.statusText}`;
      let errorDetail: string | null = null;

      // Try to extract more detailed error message from response
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorDetail = errorData.detail;
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorDetail = errorData;
          errorMessage = errorData;
        } else if (errorData.message) {
          errorDetail = errorData.message;
          errorMessage = errorData.message;
        }
      } catch {
        // If response is not JSON, use status text
      }

      // Log full error details for debugging
      console.error('Lesson start error:', {
        status: response.status,
        statusText: response.statusText,
        detail: errorDetail,
        lessonId,
      });

      if (practice && response.status === 409) {
        throw new Error(
          errorDetail || 'There is nothing to practise right now.'
        );
      }
      if (response.status === 404) {
        throw new Error('Lesson not found');
      }
      if (response.status === 403) {
        throw new Error(
          'Context is locked. Please complete prerequisites first.'
        );
      }
      if (response.status === 422) {
        throw new Error(`Validation error: ${errorMessage}`);
      }
      if (response.status === 500) {
        // For 500 errors, show more helpful message
        throw new Error(
          errorDetail ||
            'Server error occurred. Please check the browser console for details.'
        );
      }
      throw new Error(errorMessage);
    }

    startKeys.done(startPath);
    const data: StartLessonResponse = await response.json();
    return data;
  };

  const getResumable = async (
    lessonId: string,
    signal?: AbortSignal
  ): Promise<ResumableResponse> => {
    const response = await apiFetch(`/lessons/${lessonId}/resumable`, {
      signal,
    });
    if (!response.ok) {
      throw new Error(
        `Failed to check for a run to resume: ${response.status}`
      );
    }
    return response.json();
  };

  const resumeRun = async (
    runId: string,
    signal?: AbortSignal
  ): Promise<StartLessonResponse> => {
    const response = await apiFetch(`/lessons/${runId}/resume`, {
      method: 'POST',
      headers: { 'Idem-Key': crypto.randomUUID() },
      signal,
    });
    if (await isNotResumable(response)) {
      throw new ResumeExpiredError();
    }
    if (!response.ok) {
      throw new Error(`Failed to resume lesson: ${response.statusText}`);
    }
    return response.json();
  };

  const nextStep = async (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult,
    signal?: AbortSignal
  ): Promise<NextStepResponse> => {
    // Check if aborted before doing any async work
    if (signal?.aborted) {
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }

    // Build request body: include ASR result if provided, otherwise use advance event.
    // Send confidence as null (not 0) when the engine has no trustworthy score
    // (native on-device): the backend then judges by transcript match rather than
    // treating a missing score as a low-confidence failure.
    const requestBody = asrResult?.askHelp
      ? { event: 'ask_help' }
      : asrResult
        ? {
            transcript: asrResult.transcript ?? '',
            confidence:
              typeof asrResult.confidence === 'number'
                ? asrResult.confidence
                : null,
          }
        : { event: 'advance' };

    console.log('[useLessonApi] nextStep request:', {
      runId,
      stepVersion,
      hasAsrResult: !!asrResult,
      requestBody,
    });

    const nextPath = practice
      ? `/practice/${runId}/next`
      : `/lessons/${runId}/next`;
    const response = await apiFetch(nextPath, {
      method: 'POST',
      headers: {
        'X-Step-Version': stepVersion.toString(),
        'Idem-Key': advanceKey(runId, stepVersion, requestBody),
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      let errorMessage = `Failed to advance lesson: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } catch {
        // If response is not JSON, use status text
      }

      if (response.status === 409) {
        // Version conflict - reload lesson state
        console.error('Version conflict:', errorMessage);
        throw new Error(`Version conflict: ${errorMessage}`);
      }

      console.error('Failed to advance lesson:', errorMessage);
      throw new Error(errorMessage);
    }

    const data: NextStepResponse = await response.json();
    return data;
  };

  const generateContent = async (
    runId: string,
    stepVersion: number,
    signal?: AbortSignal
  ): Promise<GenerateContentResponse> => {
    const response = await apiFetch(`/lessons/${runId}/content`, {
      method: 'POST',
      headers: { 'X-Step-Version': stepVersion.toString() },
      body: JSON.stringify({}),
      signal,
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const errBody = await response.json();
        if (errBody?.detail && typeof errBody.detail === 'string') {
          message = errBody.detail;
        }
      } catch {
        // ignore
      }
      throw new Error(`Failed to generate content: ${message}`);
    }

    const data: GenerateContentResponse = await response.json();
    return data;
  };

  const fetchAudioUrl = async (
    formId: string,
    speed: 'natural' | 'slow',
    signal?: AbortSignal
  ): Promise<string> => {
    console.log(
      `[fetchAudioUrl] Fetching audio for formId: ${formId}, speed: ${speed}`
    );
    const response = await apiFetch(`/audio/forms/${formId}?speed=${speed}`, {
      signal,
    });

    console.log(
      `[fetchAudioUrl] Response status: ${response.status}, ok: ${response.ok}`
    );

    if (!response.ok) {
      const tooFast = await rateLimitError(response);
      if (tooFast) {
        throw tooFast;
      }
      const errorText = await response.text();
      console.error(`[fetchAudioUrl] Error response: ${errorText}`);
      throw new Error(`Failed to fetch audio: ${response.statusText}`);
    }

    const data: AudioUrlResponse = await response.json();
    console.log(`[fetchAudioUrl] Audio URL received: ${data.url}`);
    return data.url;
  };

  const transcribeAudio = (
    audioBlob: Blob,
    languageCode: string = 'el-GR',
    signal?: AbortSignal,
    phraseHints?: string[] | null
  ): Promise<TranscribeAudioResponse> =>
    transcribeAudioUtil(audioBlob, languageCode, signal, phraseHints);

  return {
    startLesson,
    getResumable,
    resumeRun,
    nextStep,
    generateContent,
    fetchAudioUrl,
    transcribeAudio,
    askHelp,
  };
};
